import React, { useState } from 'react';
import { supabase } from '../../api/supabase';

/**
 * Prompts for an action passcode and verifies it against any active staff member's own
 * passcode — not restricted to admin/sadmin, since a staff member can be individually
 * authorized for a specific gated action (e.g. finalizing costing) via their own passcode.
 * Usage:
 *   const { prompt, PasscodeModal } = usePasscode();
 *   await prompt();  // resolves if correct, throws if cancelled or wrong
 */
export function usePasscode() {
  const [state, setState] = useState(null); // { resolve, reject }
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  function prompt() {
    return new Promise((resolve, reject) => {
      setInput('');
      setError('');
      setState({ resolve, reject });
    });
  }

  async function handleConfirm() {
    if (!input.trim()) { setError('Enter the passcode.'); return; }
    setChecking(true);
    const { data } = await supabase.from('staff')
      .select('id')
      .eq('is_active', true)
      .eq('action_passcode', input.trim())
      .limit(1);
    setChecking(false);
    if (data?.length) {
      setState(null);
      state.resolve();
    } else {
      setError('Incorrect passcode.');
    }
  }

  function handleCancel() {
    setState(null);
    state?.reject(new Error('cancelled'));
  }

  const Modal = state ? (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: 10, padding: 28, width: 360, boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Passcode Required</div>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
          This action requires an authorized passcode. Enter it below to proceed.
        </div>
        <input
          className="form-input"
          type="password"
          placeholder="Enter passcode…"
          value={input}
          autoFocus
          onChange={e => { setInput(e.target.value); setError(''); }}
          onKeyDown={e => e.key === 'Enter' && handleConfirm()}
          style={{ marginBottom: 8 }}
        />
        {error && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 8 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button className="btn btn-danger" onClick={handleConfirm} disabled={checking}>
            {checking ? 'Checking…' : 'Confirm'}
          </button>
          <button className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
        </div>
      </div>
    </div>
  ) : null;

  return { prompt, PasscodeModal: Modal };
}
